// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import Popover from '@ta/overlays.popover';
import { Backdrop } from '@ta/modal.core';
import type { ViewportCategory } from '@ta/common.state';
import WindowResize from '@ta/events.window-resize';
import { InteractionLogger } from '@ta/tracking.interactions';
import { TopLevelNavLink } from '@ta/brand.global-nav';
import type { QuickLinksMoreMenuData } from './types';
import styles from './QuickLinksMoreMenu.less';

type Props = {|
  /** Data needed to show the collapsed links (e.g., the links) */
  collapsedData: QuickLinksMoreMenuData,

  /** Whether to align the popover to the right edge. Necessary for smaller devices with no gutter space because popover doesn't auto-correct. */
  alignToRightEdge?: boolean,

  /** Set popover as fixed. Necessary if the menu trigger is in a fixed header and we're scrolling around. */
  fixedPopover?: boolean,

  /** Trigger node. Varies depend on implementation of quick links, so let the parent define */
  children: React.Node,
|};

type InternalProps = {|
  ...Props,
  viewport: ViewportCategory, // Internal prop. Read from redux
|};

type State = {|
  arrowShift: number,
|};

/** Buffer the popover on mobile and tablet a bit */
const MOBILE_TABLET_EDGE_BUFFER = 8;

/** The popover containing all of the collapsed links */
class QuickLinksMoreMenu extends React.Component<InternalProps, State> {
  static defaultProps = {
    alignToRightEdge: false,
    fixedPopover: false,
  };

  constructor(props: InternalProps) {
    super(props);

    this.state = {
      /** A hack to move the arrow over enough (tile width / 2) until WP-563 is addressed. Updates on mount/resize. */
      arrowShift: 0,
    };

    this.wrapEl = React.createRef();
  }

  componentDidMount() {
    this.updateArrowPos();
  }

  /** A hack to move the arrow over enough (tile width / 2) until WP-563 is addressed. */
  updateArrowPos = () => {
    const el = this.wrapEl.current;
    if (el) {
      // Even hackier than what we're already doing: the 10 is to get the arrow to center better.
      this.setState({ arrowShift: (-1 * el.offsetWidth) / 2 + 10 + MOBILE_TABLET_EDGE_BUFFER });
    }
  };

  menuContents = () =>
    this.props.collapsedData && (
      <ul className={styles.list}>
        {this.props.collapsedData.links.map(link => (
          <li key={link.id} className={styles.item}>
            <InteractionLogger>
              {logInteraction => (
                <TopLevelNavLink
                  link={link}
                  extraClasses={styles.link}
                  trackingModule="QuickLinks"
                  nonLink={link.id === 'tourism' || link.id === 'scoped-home'}
                  onClick={e => logInteraction(e, 'quickLinkClickV2', { trackingKey: link.id })}
                />
              )}
            </InteractionLogger>
          </li>
        ))}
      </ul>
    );

  wrapEl: { current: HTMLDivElement | null };

  render() {
    const shouldAlignRight =
      this.props.alignToRightEdge &&
      (this.props.viewport === 'MOBILE' || this.props.viewport === 'TABLET');
    return (
      <React.Fragment>
        <Popover
          below
          closeButton={false}
          shiftX={shouldAlignRight ? -1 * MOBILE_TABLET_EDGE_BUFFER : 0}
          shiftY={-11}
          shiftArrow={this.state.arrowShift}
          fullBleed
          fixed={this.props.fixedPopover}
          alignEdge={shouldAlignRight ? 'right' : undefined}
          key={this.props.viewport}
          contents={close => {
            if (this.props.viewport !== 'MOBILE') {
              return this.menuContents();
            }
            return <Backdrop onClick={close}>{this.menuContents()}</Backdrop>;
          }}
        >
          {(target, open) => (
            <div ref={this.wrapEl}>
              <div ref={target} onClick={open}>
                {this.props.children}
              </div>
            </div>
          )}
        </Popover>
        <WindowResize callback={this.updateArrowPos} />

        {/* For crawlers we show a hidden div in the page source so they're aware of what would be in the clickable popover. */}
        <div className={styles.hidden}>{this.menuContents()}</div>
      </React.Fragment>
    );
  }
}

export default connect<InternalProps, Props, _, _, _, _>(state => ({
  viewport: state.meta.device.viewportCategory,
}))(QuickLinksMoreMenu);
